import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  CheckCircle2, XCircle, ShieldCheck, Globe, Lock, KeyRound,
  Eye, EyeOff, AlertCircle, ArrowRight, Mail
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Aktivasyon = () => {
  const { token } = useParams();
  const navigate = useNavigate();

  // Adımlar: 'token_dogrulama' → 'sifre_olustur' → 'islem' → 'basarili' | 'hata'
  const [adim, setAdim] = useState('token_dogrulama');
  const [hataMesaji, setHataMesaji] = useState('');
  const [sonuc, setSonuc] = useState({ ip: '', musteriNo: '', email: '' });

  const [sifre, setSifre] = useState('');
  const [sifreTekrar, setSifreTekrar] = useState('');
  const [goster, setGoster] = useState({ yeni: false, tekrar: false });
  const [yukleniyor, setYukleniyor] = useState(false);

  // İlk girişte token'ı gerçek hemen kullanmayalım; sadece şifre ekranına geç.
  // Backend, aktivasyon endpoint'inde token validity'yi yine kontrol ediyor.
  useEffect(() => {
    if (!token) {
      setAdim('hata');
      setHataMesaji('Aktivasyon linki geçersiz');
      return;
    }
    setAdim('sifre_olustur');
  }, [token]);

  // Şifre güç göstergesi
  const sifreGuc = (() => {
    if (!sifre) return { skor: 0, etiket: '', renk: 'bg-slate-600' };
    let skor = 0;
    if (sifre.length >= 6) skor++;
    if (sifre.length >= 10) skor++;
    if (/[A-Z]/.test(sifre)) skor++;
    if (/[0-9]/.test(sifre)) skor++;
    if (/[^A-Za-z0-9]/.test(sifre)) skor++;
    const etiketler = ['Çok zayıf', 'Zayıf', 'Orta', 'İyi', 'Güçlü', 'Çok güçlü'];
    const renkler = ['bg-rose-400', 'bg-orange-400', 'bg-amber-400', 'bg-sky-400', 'bg-emerald-400', 'bg-emerald-500'];
    return { skor, etiket: etiketler[skor], renk: renkler[skor] };
  })();

  const handleAktivasyon = async () => {
    setHataMesaji('');
    if (!sifre || !sifreTekrar) {
      setHataMesaji('Lütfen şifrenizi ve tekrarını girin');
      return;
    }
    if (sifre.length < 6) {
      setHataMesaji('Şifre en az 6 karakter olmalı');
      return;
    }
    if (sifre !== sifreTekrar) {
      setHataMesaji('Şifreler eşleşmiyor');
      return;
    }

    setYukleniyor(true);
    setAdim('islem');
    try {
      const res = await axios.post(`${API}/auth/aktivasyon`, { token, yeni_sifre: sifre });
      if (res.data.success) {
        setSonuc({
          ip: res.data.ip || '-',
          musteriNo: res.data.musteri_no || '',
          email: res.data.email || ''
        });
        setAdim('basarili');
      } else {
        setHataMesaji(res.data.error || 'Aktivasyon başarısız');
        setAdim('hata');
      }
    } catch (err) {
      setHataMesaji(err.response?.data?.error || 'Bağlantı hatası, tekrar deneyin');
      setAdim('hata');
    } finally {
      setYukleniyor(false);
    }
  };

  const inputClass =
    'w-full h-12 pl-11 pr-11 bg-slate-800 border border-slate-700 focus:border-sky-400 focus:ring-2 focus:ring-sky-400/20 rounded-lg text-sm text-white outline-none transition placeholder:text-slate-500';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Arka plan dekoratif gradient */}
      <div className="absolute inset-0 opacity-30 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-sky-500 rounded-full filter blur-3xl opacity-20" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-600 rounded-full filter blur-3xl opacity-20" />
      </div>

      <div className="bg-slate-900 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden border border-slate-800 relative z-10" data-testid="aktivasyon-kart">

        {/* ── ADIM: TOKEN DOĞRULAMA ── */}
        {adim === 'token_dogrulama' && (
          <div className="p-10 text-center">
            <div className="w-12 h-12 border-3 border-sky-400 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-slate-400 text-sm">Link doğrulanıyor...</p>
          </div>
        )}

        {/* ── ADIM: ŞİFRE OLUŞTUR ── */}
        {adim === 'sifre_olustur' && (
          <>
            {/* Header */}
            <div className="bg-slate-800 border-b border-slate-700 px-6 py-5">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-lg bg-sky-400/10 border border-sky-400/30 flex items-center justify-center">
                  <KeyRound size={20} className="text-sky-400" />
                </div>
                <div>
                  <h2 className="text-base font-bold text-white">Hesabınızı Aktifleştirin</h2>
                  <p className="text-xs text-slate-400 mt-0.5">Kendi şifrenizi belirleyin</p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              <div className="bg-sky-400/10 border border-sky-400/25 rounded-lg p-3.5">
                <p className="text-xs text-slate-300 leading-relaxed">
                  Hoş geldiniz — hesabınızı aktifleştirmek için kendi şifrenizi belirleyin.
                  Bu link tek kullanımlıktır ve bağlandığınız <span className="text-sky-300 font-semibold">IP adresiniz sisteme yetkili IP olarak kaydedilecektir</span>.
                </p>
              </div>

              {/* Yeni Şifre */}
              <div>
                <label className="text-[11px] font-semibold text-slate-400 mb-1.5 block uppercase tracking-wider">
                  Yeni Şifre
                </label>
                <div className="relative">
                  <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
                  <input
                    type={goster.yeni ? 'text' : 'password'}
                    value={sifre}
                    onChange={(e) => setSifre(e.target.value)}
                    className={inputClass}
                    placeholder="En az 6 karakter"
                    data-testid="aktivasyon-sifre-input"
                    autoComplete="new-password"
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setGoster({ ...goster, yeni: !goster.yeni })}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition"
                  >
                    {goster.yeni ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {sifre && (
                  <div className="mt-2 flex items-center gap-2">
                    <div className="flex-1 h-1 bg-slate-700 rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all duration-300 ${sifreGuc.renk}`}
                        style={{ width: `${(sifreGuc.skor / 5) * 100}%` }}
                      />
                    </div>
                    <span className="text-[10px] font-semibold text-slate-400 tabular-nums w-16 text-right">
                      {sifreGuc.etiket}
                    </span>
                  </div>
                )}
              </div>

              {/* Şifre Tekrar */}
              <div>
                <label className="text-[11px] font-semibold text-slate-400 mb-1.5 block uppercase tracking-wider">
                  Şifre Tekrar
                </label>
                <div className="relative">
                  <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
                  <input
                    type={goster.tekrar ? 'text' : 'password'}
                    value={sifreTekrar}
                    onChange={(e) => setSifreTekrar(e.target.value)}
                    className={inputClass}
                    placeholder="Şifrenizi tekrar girin"
                    data-testid="aktivasyon-sifre-tekrar-input"
                    autoComplete="new-password"
                    onKeyDown={(e) => { if (e.key === 'Enter') handleAktivasyon(); }}
                  />
                  <button
                    type="button"
                    onClick={() => setGoster({ ...goster, tekrar: !goster.tekrar })}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition"
                  >
                    {goster.tekrar ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {sifreTekrar && sifre && (
                  <div className="mt-1.5 flex items-center gap-1.5">
                    {sifreTekrar === sifre ? (
                      <>
                        <CheckCircle2 size={12} className="text-emerald-400" />
                        <span className="text-[11px] text-emerald-300 font-medium">Şifreler eşleşiyor</span>
                      </>
                    ) : (
                      <>
                        <AlertCircle size={12} className="text-rose-400" />
                        <span className="text-[11px] text-rose-300 font-medium">Şifreler eşleşmiyor</span>
                      </>
                    )}
                  </div>
                )}
              </div>

              {hataMesaji && (
                <div className="bg-rose-500/10 border border-rose-500/30 rounded-lg p-3 flex items-start gap-2">
                  <AlertCircle size={14} className="text-rose-400 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-rose-300">{hataMesaji}</p>
                </div>
              )}

              <button
                onClick={handleAktivasyon}
                disabled={yukleniyor || !sifre || !sifreTekrar}
                data-testid="aktivasyon-btn"
                className={`w-full py-3.5 rounded-lg text-sm font-semibold transition flex items-center justify-center gap-2 ${
                  yukleniyor || !sifre || !sifreTekrar
                    ? 'bg-slate-700 text-slate-500 cursor-not-allowed'
                    : 'bg-sky-500 hover:bg-sky-400 text-white shadow-lg shadow-sky-500/20'
                }`}
              >
                <ShieldCheck size={16} />
                {yukleniyor ? 'Aktifleştiriliyor...' : 'Hesabımı Aktifleştir'}
              </button>

              <p className="text-[10px] text-center text-slate-500">
                Bu işlem sonrası IP adresiniz sisteme kaydedilecek ve yalnızca bu IP'den giriş yapabileceksiniz.
              </p>
            </div>
          </>
        )}

        {/* ── ADIM: İŞLEM ── */}
        {adim === 'islem' && (
          <div className="p-10 text-center">
            <div className="w-14 h-14 border-4 border-sky-400 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-slate-300 text-sm font-medium">Hesabınız aktifleştiriliyor...</p>
            <p className="text-slate-500 text-xs mt-1">IP adresiniz kaydediliyor</p>
          </div>
        )}

        {/* ── ADIM: BAŞARILI ── */}
        {adim === 'basarili' && (
          <>
            {/* Header — sky success */}
            <div className="bg-gradient-to-br from-sky-500 to-sky-600 p-7 text-white text-center">
              <div className="w-16 h-16 rounded-full bg-white/15 backdrop-blur flex items-center justify-center mx-auto mb-3 border border-white/20">
                <CheckCircle2 className="w-9 h-9" strokeWidth={2.5} />
              </div>
              <h2 className="text-xl font-bold">Hesabınız Aktif!</h2>
              <p className="text-sky-100 text-xs mt-1">YETSAN Panel'e hoş geldiniz</p>
            </div>

            <div className="p-6 space-y-3">
              {sonuc.musteriNo && (
                <div className="bg-slate-800 rounded-xl p-4 text-center border border-slate-700">
                  <p className="text-slate-500 text-[10px] uppercase tracking-[0.15em] mb-1 font-semibold">
                    Müşteri Numaranız
                  </p>
                  <p className="text-sky-300 font-mono text-2xl font-bold tracking-[0.2em]">
                    {sonuc.musteriNo}
                  </p>
                </div>
              )}

              <div className="bg-slate-800/60 rounded-xl p-4 border border-slate-700 space-y-3">
                {sonuc.email && (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-slate-400">
                      <Mail size={13} />
                      <span className="text-xs">E-posta</span>
                    </div>
                    <span className="text-xs text-slate-200 font-medium truncate max-w-[200px]">
                      {sonuc.email}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-slate-400">
                    <Globe size={13} />
                    <span className="text-xs">Yetkili IP</span>
                  </div>
                  <span className="font-mono text-xs font-bold text-sky-300 bg-sky-400/10 border border-sky-400/20 px-2 py-0.5 rounded">
                    {sonuc.ip}
                  </span>
                </div>
              </div>

              <div className="bg-amber-500/10 border border-amber-500/25 rounded-lg p-3 flex items-start gap-2">
                <Lock size={13} className="text-amber-400 flex-shrink-0 mt-0.5" />
                <p className="text-[11px] text-amber-200/90 leading-relaxed">
                  Farklı bir lokasyondan giriş yapmak için firma yöneticinizle iletişime geçin.
                </p>
              </div>

              <button
                onClick={() => navigate('/login')}
                data-testid="aktivasyon-giris-btn"
                className="w-full bg-sky-500 hover:bg-sky-400 text-white py-3 rounded-lg font-semibold text-sm transition flex items-center justify-center gap-2 shadow-lg shadow-sky-500/20"
              >
                Giriş Yap
                <ArrowRight size={16} />
              </button>
            </div>
          </>
        )}

        {/* ── ADIM: HATA ── */}
        {adim === 'hata' && (
          <>
            <div className="bg-gradient-to-br from-rose-500 to-rose-600 p-7 text-white text-center">
              <div className="w-16 h-16 rounded-full bg-white/15 backdrop-blur flex items-center justify-center mx-auto mb-3 border border-white/20">
                <XCircle className="w-9 h-9" strokeWidth={2.5} />
              </div>
              <h2 className="text-xl font-bold">Aktivasyon Başarısız</h2>
            </div>

            <div className="p-6 space-y-4">
              <p className="text-slate-300 text-center text-sm">
                {hataMesaji || 'Aktivasyon linki geçersiz veya daha önce kullanılmış olabilir.'}
              </p>

              <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
                <p className="font-semibold text-slate-200 text-xs mb-2">Ne yapabilirsiniz?</p>
                <ul className="text-[11px] text-slate-400 space-y-1 ml-4 list-disc">
                  <li>Firma yöneticinizle iletişime geçin</li>
                  <li>Yeni aktivasyon linki talep edin</li>
                  <li>E-postanızın spam/junk klasörünü kontrol edin</li>
                </ul>
              </div>

              <button
                onClick={() => navigate('/login')}
                className="w-full bg-slate-700 hover:bg-slate-600 text-white py-3 rounded-lg font-semibold text-sm transition"
              >
                Giriş Sayfasına Dön
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Aktivasyon;
